import {
    Document,
    Paragraph,
    TextRun,
    Table,
    TableCell,
    TableRow,
    WidthType,
    AlignmentType,
    HeadingLevel,
    BorderStyle,
    Header,
    ImageRun,
    TableOfContents,
    PageNumber,
    Footer,
    convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";
import arivuLogo from "../../assets/logo/ArivuHeader.png";

// Helper function to convert image to base64
const getBase64Image = async (imagePath) => {
    try {
        const response = await fetch(imagePath);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Error loading image:", error);
        return null;
    }
};

// Helper function to create a heading with proper styling
const createHeading = (text, level = HeadingLevel.HEADING_1) => {
    const color = level === HeadingLevel.HEADING_1 ? "4C7F25" : level === HeadingLevel.HEADING_2 ? "608A37" : "000000";
    return new Paragraph({
        children: [
            new TextRun({
                text: text,
                bold: true,
                color: color,
                font: "Poppins",
                size: level === HeadingLevel.HEADING_1 ? 32 : level === HeadingLevel.HEADING_2 ? 28 : 24,
            }),
        ],
        heading: level,
        spacing: { before: 400, after: 200 },
    });
};

// Helper function to create a paragraph
const createParagraph = (text, bold = false, italics = false) => {
    return new Paragraph({
        children: [
            new TextRun({
                text: text,
                bold: bold,
                italics: italics,
                font: "Poppins",
                size: 22,
            }),
        ],
        spacing: { before: 100, after: 100 },
    });
};

// Helper function to create a table with Arivu brand colors
const createTable = (headers, data) => {
    const headerRow = new TableRow({
        children: headers.map(
            (header) =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: header,
                                    bold: true,
                                    color: "FFFFFF",
                                    font: "Poppins",
                                    size: 22,
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                    shading: {
                        fill: "4C7F25", // Dark green - Arivu brand color
                    },
                    margins: {
                        top: convertInchesToTwip(0.05),
                        bottom: convertInchesToTwip(0.05),
                        left: convertInchesToTwip(0.08),
                        right: convertInchesToTwip(0.08),
                    },
                })
        ),
    });

    const dataRows = data.map((row, rowIndex) =>
        new TableRow({
            children: row.map(
                (cell) =>
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: cell?.toString() || "",
                                        font: "Poppins",
                                        size: 20,
                                    }),
                                ],
                                alignment: AlignmentType.LEFT,
                            }),
                        ],
                        shading: {
                            fill: rowIndex % 2 === 0 ? "ebf1de" : "f5f9f0", // Alternating light green shades
                        },
                        margins: {
                            top: convertInchesToTwip(0.05),
                            bottom: convertInchesToTwip(0.05),
                            left: convertInchesToTwip(0.08),
                            right: convertInchesToTwip(0.08),
                        },
                    })
            ),
        })
    );

    return new Table({
        width: {
            size: 100,
            type: WidthType.PERCENTAGE,
        },
        rows: [headerRow, ...dataRows],
        borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "4C7F25" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "4C7F25" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "4C7F25" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "4C7F25" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
        },
    });
};

// Generate the Organization Report Document
export const generateOrganizationReport = async (organizationData) => {
    // Load the logo image
    const logoBase64 = await getBase64Image(arivuLogo);

    const sections = [];

    // Title Page
    sections.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: "Organization Report",
                    bold: true,
                    font: "Poppins",
                    size: 48,
                    color: "4C7F25",
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 1200, after: 400 },
        }),
        new Paragraph({
            children: [
                new TextRun({
                    text: organizationData.name || "N/A",
                    bold: true,
                    font: "Poppins",
                    size: 32,
                    color: "000000",
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 800 },
        }),
        new Paragraph({
            text: "",
            pageBreakBefore: true,
        })
    );

    // Table of Contents Page
    sections.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: "Table of Contents",
                    bold: true,
                    font: "Poppins",
                    size: 36,
                    color: "4C7F25",
                }),
            ],
            spacing: { before: 400, after: 400 },
        }),
        new TableOfContents("Table of Contents", {
            hyperlink: true,
            headingStyleRange: "1-3",
        }),
        new Paragraph({
            text: "",
            pageBreakBefore: true,
        })
    );

    // Essential Indicators
    sections.push(
        createHeading("I. Essential Indicators", HeadingLevel.HEADING_1),

        // 1. Energy Consumption and Intensity
        createHeading("1. Details of total energy consumption and energy intensity", HeadingLevel.HEADING_2),
        createParagraph("Total electricity consumed (in GJ)"),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["Total Electricity Consumed", organizationData.totalElectricity || 0, organizationData.prevTotalElectricity || 0],
                ["Total Fuel Consumed", organizationData.totalFuel || 0, organizationData.prevTotalFuel || 0],
                ["Energy from Other Sources", organizationData.otherEnergy || 0, organizationData.prevOtherEnergy || 0],
                ["Total Energy Consumed", organizationData.totalEnergy || 0, organizationData.prevTotalEnergy || 0],
                ["Energy Intensity per Rupee of Turnover", organizationData.energyIntensity || 0, organizationData.prevEnergyIntensity || 0],
            ]
        ),

        // 2. PAT Scheme
        createHeading(
            "2. Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India?",
            HeadingLevel.HEADING_2
        ),
        createParagraph(organizationData.patScheme || "No information provided"),

        // 3. Water Disclosures
        createHeading("3. Provide details of the following disclosures related to water", HeadingLevel.HEADING_2),
        createParagraph("Water withdrawal by source (in kilolitres)"),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["Surface Water", organizationData.surfaceWater || 0, organizationData.prevSurfaceWater || 0],
                ["Groundwater", organizationData.groundwater || 0, organizationData.prevGroundwater || 0],
                ["Third Party Water", organizationData.thirdPartyWater || 0, organizationData.prevThirdPartyWater || 0],
                ["Seawater / Desalinated Water", organizationData.seawater || 0, organizationData.prevSeawater || 0],
                ["Others", organizationData.otherWater || 0, organizationData.prevOtherWater || 0],
                ["Total Volume of Water Withdrawal", organizationData.totalWater || 0, organizationData.prevTotalWater || 0],
                ["Total Volume of Water Consumption", organizationData.waterConsumption || 0, organizationData.prevWaterConsumption || 0],
                ["Water Intensity per Rupee of Turnover", organizationData.waterIntensity || 0, organizationData.prevWaterIntensity || 0],
            ]
        ),

        // 4. Zero Liquid Discharge
        createHeading("4. Has the entity implemented a mechanism for Zero Liquid Discharge?", HeadingLevel.HEADING_2),
        createParagraph(organizationData.zeroLiquidDischarge || "No information provided"),

        // 5. Air Emissions
        createHeading("5. Please provide details of air emissions (other than GHG emissions) by the entity", HeadingLevel.HEADING_2),
        createTable(
            ["Parameter", "Unit", "Current FY", "Previous FY"],
            [
                ["NOx", "Kg", organizationData.nox || 0, organizationData.prevNox || 0],
                ["SOx", "Kg", organizationData.sox || 0, organizationData.prevSox || 0],
                ["Particulate Matter (PM)", "Kg", organizationData.pm || 0, organizationData.prevPm || 0],
                ["Persistent Organic Pollutants (POP)", "Kg", organizationData.pop || 0, organizationData.prevPop || 0],
                ["Volatile Organic Compounds (VOC)", "Kg", organizationData.voc || 0, organizationData.prevVoc || 0],
                ["Hazardous Air Pollutants (HAP)", "Kg", organizationData.hap || 0, organizationData.prevHap || 0],
                ["Others", "Kg", organizationData.otherAir || 0, organizationData.prevOtherAir || 0],
            ]
        ),

        // 6. GHG Emissions
        createHeading("6. Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions)", HeadingLevel.HEADING_2),
        createTable(
            ["Parameter", "Unit", "Current FY", "Previous FY"],
            [
                ["Total Scope 1 Emissions", "Metric tonnes of CO2e", organizationData.scope1 || 0, organizationData.prevScope1 || 0],
                ["Total Scope 2 Emissions", "Metric tonnes of CO2e", organizationData.scope2 || 0, organizationData.prevScope2 || 0],
                [
                    "Total Scope 1 + Scope 2 Emissions",
                    "Metric tonnes of CO2e",
                    (organizationData.scope1 || 0) + (organizationData.scope2 || 0),
                    (organizationData.prevScope1 || 0) + (organizationData.prevScope2 || 0),
                ],
                ["GHG Intensity per Rupee of Turnover", "Total emissions / Turnover", organizationData.ghgIntensity || 0, organizationData.prevGhgIntensity || 0],
            ]
        ),

        // 7. GHG Reduction Projects
        createHeading("7. Does the entity have any project related to reducing Green House Gas emission?", HeadingLevel.HEADING_2),
        createParagraph(organizationData.ghgReductionProjects || "No information provided"),

        // 8. Waste Management
        createHeading("8. Provide details related to waste management by the entity", HeadingLevel.HEADING_2),
        createParagraph("Total Waste Generated (in metric tonnes)"),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["Plastic Waste", organizationData.plasticWaste || 0, organizationData.prevPlasticWaste || 0],
                ["E-waste", organizationData.ewaste || 0, organizationData.prevEwaste || 0],
                ["Bio-medical Waste", organizationData.biomedicalWaste || 0, organizationData.prevBiomedicalWaste || 0],
                ["Construction and Demolition Waste", organizationData.constructionWaste || 0, organizationData.prevConstructionWaste || 0],
                ["Battery Waste", organizationData.batteryWaste || 0, organizationData.prevBatteryWaste || 0],
                ["Radioactive Waste", organizationData.radioactiveWaste || 0, organizationData.prevRadioactiveWaste || 0],
                ["Other Hazardous Waste", organizationData.otherHazardous || 0, organizationData.prevOtherHazardous || 0],
                ["Other Non-hazardous Waste", organizationData.otherNonHazardous || 0, organizationData.prevOtherNonHazardous || 0],
            ]
        ),
        createParagraph("Total Waste Recovered (in metric tonnes)"),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["Recycled", organizationData.recycled || 0, organizationData.prevRecycled || 0],
                ["Re-used", organizationData.reused || 0, organizationData.prevReused || 0],
                ["Other Recovery Operations", organizationData.otherRecovery || 0, organizationData.prevOtherRecovery || 0],
            ]
        ),
        createParagraph("Total Waste Disposed (in metric tonnes)"),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["Incineration", organizationData.incineration || 0, organizationData.prevIncineration || 0],
                ["Landfilling", organizationData.landfilling || 0, organizationData.prevLandfilling || 0],
                ["Other Disposal Operations", organizationData.otherDisposal || 0, organizationData.prevOtherDisposal || 0],
            ]
        ),

        // 9. Waste Management Practices
        createHeading("9. Briefly describe the waste management practices adopted in your establishments", HeadingLevel.HEADING_2),
        createParagraph(organizationData.wasteManagementPractices || "No information provided"),

        // 10. Ecologically Sensitive Areas
        createHeading("10. If the entity has operations/offices in/around ecologically sensitive areas", HeadingLevel.HEADING_2),
        createTable(
            ["S.No.", "Location of operations/offices", "Type of operations", "Whether the conditions of environmental approval/clearance are being complied with?"],
            organizationData.ecologicalAreas || [["No data available", "", "", ""]]
        ),

        // 11. Environmental Impact Assessments
        createHeading("11. Details of environmental impact assessments of projects undertaken by the entity", HeadingLevel.HEADING_2),
        createTable(
            ["Name and brief details of project", "EIA Notification No.", "Date", "Whether conducted by independent external agency", "Results communicated in public domain"],
            organizationData.eiaProjects || [["No data available", "", "", "", ""]]
        ),

        // 12. Environmental Compliance
        createHeading("12. Is the entity compliant with the applicable environmental law/regulations/guidelines in India?", HeadingLevel.HEADING_2),
        createParagraph(organizationData.environmentalCompliance || "No information provided"),
        createTable(
            ["S.No.", "Specify the law/regulation/guideline", "Provide details of the non-compliance", "Any fines/penalties/action taken"],
            organizationData.complianceDetails || [["No data available", "", "", ""]]
        )
    );

    // Leadership Indicators
    sections.push(
        new Paragraph({
            text: "",
            pageBreakBefore: true,
        }),
        createHeading("II. Leadership Indicators", HeadingLevel.HEADING_1),

        // 1. Renewable vs Non-Renewable Energy
        createHeading("1. Provide break-up of the total energy consumed from renewable and non-renewable sources", HeadingLevel.HEADING_2),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["From Renewable Sources - Total Electricity", organizationData.renewableElectricity || 0, organizationData.prevRenewableElectricity || 0],
                ["From Renewable Sources - Total Fuel", organizationData.renewableFuel || 0, organizationData.prevRenewableFuel || 0],
                ["From Renewable Sources - Others", organizationData.renewableOthers || 0, organizationData.prevRenewableOthers || 0],
                ["From Non-Renewable Sources - Total Electricity", organizationData.nonRenewableElectricity || 0, organizationData.prevNonRenewableElectricity || 0],
                ["From Non-Renewable Sources - Total Fuel", organizationData.nonRenewableFuel || 0, organizationData.prevNonRenewableFuel || 0],
                ["From Non-Renewable Sources - Others", organizationData.nonRenewableOthers || 0, organizationData.prevNonRenewableOthers || 0],
            ]
        ),

        // 2. Water Discharge
        createHeading("2. Provide details of water discharge by the entity", HeadingLevel.HEADING_2),
        createTable(
            ["Parameter", "Current FY", "Previous FY"],
            [
                ["To Surface Water - No Treatment", organizationData.dischargeSurfaceNoTreatment || 0, organizationData.prevDischargeSurfaceNoTreatment || 0],
                ["To Surface Water - With Treatment", organizationData.dischargeSurfaceWithTreatment || 0, organizationData.prevDischargeSurfaceWithTreatment || 0],
                ["To Groundwater - No Treatment", organizationData.dischargeGroundNoTreatment || 0, organizationData.prevDischargeGroundNoTreatment || 0],
                ["To Groundwater - With Treatment", organizationData.dischargeGroundWithTreatment || 0, organizationData.prevDischargeGroundWithTreatment || 0],
                ["To Seawater - No Treatment", organizationData.dischargeSeaNoTreatment || 0, organizationData.prevDischargeSeaNoTreatment || 0],
                ["To Seawater - With Treatment", organizationData.dischargeSeaWithTreatment || 0, organizationData.prevDischargeSeaWithTreatment || 0],
                ["Sent to Third-Parties - No Treatment", organizationData.dischargeThirdPartyNoTreatment || 0, organizationData.prevDischargeThirdPartyNoTreatment || 0],
                ["Sent to Third-Parties - With Treatment", organizationData.dischargeThirdPartyWithTreatment || 0, organizationData.prevDischargeThirdPartyWithTreatment || 0],
            ]
        ),

        // 3. Water Stress (Optional)
        createHeading("3. Water withdrawal, consumption and discharge in areas of water stress (Optional)", HeadingLevel.HEADING_2),
        createParagraph(organizationData.waterStressDetails || "No data available for this optional indicator"),

        // 4. Scope 3 Emissions
        createHeading("4. Please provide details of total Scope 3 emissions & its intensity", HeadingLevel.HEADING_2),
        createTable(
            ["Parameter", "Unit", "Current FY", "Previous FY"],
            [
                ["Total Scope 3 Emissions", "Metric tonnes of CO2e", organizationData.scope3 || 0, organizationData.prevScope3 || 0],
                ["Scope 3 Intensity per Rupee of Turnover", "", organizationData.scope3Intensity || 0, organizationData.prevScope3Intensity || 0],
            ]
        ),

        // 5. Biodiversity Impact
        createHeading("5. With respect to the ecologically sensitive areas reported at Question 10 of Essential Indicators", HeadingLevel.HEADING_2),
        createParagraph("Significant direct & indirect impact of the entity on biodiversity:"),
        createParagraph(organizationData.biodiversityImpact || "No information provided"),

        // 6. Resource Efficiency Initiatives
        createHeading("6. If the entity has undertaken any specific initiatives or used innovative technology", HeadingLevel.HEADING_2),
        createTable(
            ["S.No.", "Initiative undertaken", "Details of the initiative", "Outcome of the initiative"],
            organizationData.resourceInitiatives || [["No data available", "", "", ""]]
        ),

        // 7. Business Continuity & Disaster Management
        createHeading("7. Does the entity have a business continuity and disaster management plan?", HeadingLevel.HEADING_2),
        createParagraph(organizationData.disasterManagementPlan || "No information provided"),

        // 8. Value Chain Impact
        createHeading("8. Disclose any significant adverse impact arising from value chain of the entity", HeadingLevel.HEADING_2),
        createParagraph(organizationData.valueChainImpact || "No information provided"),

        // 9. Value Chain Assessment
        createHeading("9. Percentage of value chain partners assessed for environmental impacts", HeadingLevel.HEADING_2),
        createParagraph(organizationData.valueChainAssessment || "No information provided")
    );

    // Create header with Arivu logo
    const headerChildren = [];
    if (logoBase64) {
        headerChildren.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        data: logoBase64,
                        transformation: {
                            width: 100,
                            height: 40,
                        },
                    }),
                ],
                alignment: AlignmentType.RIGHT,
            })
        );
    }

    // Create footer with page numbers
    const footer = new Footer({
        children: [
            new Paragraph({
                children: [
                    new TextRun({
                        children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
                        font: "Poppins",
                        size: 20,
                    }),
                ],
                alignment: AlignmentType.CENTER,
            }),
        ],
    });

    // Create the document with green background
    const doc = new Document({
        background: {
            color: "f8fdf5", // Very light green background
        },
        styles: {
            default: {
                heading1: {
                    run: {
                        font: "Poppins",
                        size: 32,
                        bold: true,
                        color: "4C7F25",
                    },
                    paragraph: {
                        spacing: { before: 400, after: 200 },
                    },
                },
                heading2: {
                    run: {
                        font: "Poppins",
                        size: 28,
                        bold: true,
                        color: "608A37",
                    },
                    paragraph: {
                        spacing: { before: 300, after: 150 },
                    },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: convertInchesToTwip(1),
                            right: convertInchesToTwip(1),
                            bottom: convertInchesToTwip(1),
                            left: convertInchesToTwip(1),
                        },
                    },
                },
                headers: {
                    default: new Header({
                        children: headerChildren,
                    }),
                },
                footers: {
                    default: footer,
                },
                children: sections,
            },
        ],
    });

    return doc;
};

// Export function to generate and download the Word document
export const exportOrganizationReportAsWord = async (organizationData) => {
    try {
        const doc = await generateOrganizationReport(organizationData);
        const blob = await import("docx").then((module) => module.Packer.toBlob(doc));
        const fileName = `Organization_Report_${organizationData.name || "Unknown"}_${organizationData.year || new Date().getFullYear()}.docx`;
        saveAs(blob, fileName);
        return true;
    } catch (error) {
        console.error("Error generating Word document:", error);
        return false;
    }
};
